import { stringify } from 'csv-stringify/browser/esm/sync';
import Debug from 'debug';
import { Dispatch } from 'redux';
import { Entity, FieldType, getFieldArray, getRowLabel, isBlank, ReferenceField, Row } from 'gossamer-unified';
import { GCacheState, selectRecordFromGCache, getRecordFromGCache, GCacheStore } from './ReduxGCache';

const debug = Debug('gfe/da/copyData');

/**
 * Pre-fetch all reference field data for rows
 *
 * @param store - Redux store with dispatch and getState
 * @param entity - Entity definition
 * @param rows - Data rows that may contain references
 * @param waitForCache - Whether to wait for cache to be populated (defaults to true)
 * @returns Promise that resolves when all references are fetched
 */
export const prefetchReferenceData = async <T extends Row>(
  store: GCacheStore,
  entity: Entity<T>,
  rows: T[],
  waitForCache: boolean = true,
): Promise<void> => {
  if (!rows || rows.length === 0) {
    return;
  }

  // Collect all reference fields and their values
  const referenceFields = getFieldArray(entity)
    .filter(([field, _]) => field.listColumn && field.type === FieldType.Reference)
    .map(([field, fieldId]) => ({ field: field as ReferenceField, fieldId }));

  if (referenceFields.length === 0) {
    return;
  }

  // Collect unique reference IDs per entity
  const referencesToFetch = new Map<string, Set<string>>();

  referenceFields.forEach(({ field, fieldId }) => {
    const refEntityId = field.refEntity.id;
    if (!referencesToFetch.has(refEntityId)) {
      referencesToFetch.set(refEntityId, new Set());
    }

    const refSet = referencesToFetch.get(refEntityId)!;
    rows.forEach((row) => {
      const value = (row as Record<string, unknown>)[fieldId];
      if (!isBlank(value)) {
        refSet.add(String(value));
      }
    });
  });

  // Dispatch fetch actions for all references
  referencesToFetch.forEach((keys, entityId) => {
    const refField = referenceFields.find((rf) => rf.field.refEntity.id === entityId);
    if (!refField) return;

    keys.forEach((key) => {
      debug(`Dispatching getRecordFromGCache for ${entityId}/${key}`);
      store.dispatch(
        getRecordFromGCache({
          entity: refField.field.refEntity,
          key,
        }),
      );
    });
  });

  // Wait for cache to be populated if requested
  if (waitForCache) {
    // Poll the cache until all records are loaded or timeout
    const maxWaitMs = 5000;
    const pollIntervalMs = 100;
    const startTime = Date.now();

    const allRecordsLoaded = (): boolean => {
      const state = store.getState();
      for (const [entityId, keys] of referencesToFetch.entries()) {
        for (const key of keys) {
          const record = selectRecordFromGCache(state, entityId, key);
          if (!record || (record.isFetching && !record.data && !record.error)) {
            return false;
          }
        }
      }
      return true;
    };

    while (!allRecordsLoaded() && Date.now() - startTime < maxWaitMs) {
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }

    if (!allRecordsLoaded()) {
      debug('Warning: Not all reference data loaded within timeout');
    } else {
      debug('All reference data loaded successfully');
    }
  }
};

type StateContainingGCache = { cache: GCacheState };
type StoreWithGCache = { dispatch: Dispatch; getState: () => StateContainingGCache };

/**
 * Generate CSV string from data rows
 *
 * @param store - Redux store with GCache state (optional, used for reference field labels)
 * @param entity - Entity definition
 * @param rows - Data rows to convert
 * @param includeHeader - Whether to include header row
 * @returns CSV string
 */
export const generateCSV = <T extends Row>(
  state: StateContainingGCache | undefined,
  entity: Entity<T>,
  rows: T[],
  includeHeader: boolean,
): string => {
  if (!rows || rows.length === 0) {
    return '';
  }
  const safeState = state || ({ cache: {} as GCacheState } as StateContainingGCache);
  const array = rows.map((row) => getRowValuesForCSV(safeState, entity, row));
  if (includeHeader) {
    array.unshift(getHeaderValuesForCSV(entity));
  }
  return stringify(array);
};

export const getHeaderValuesForCSV = <T extends Row>(entity: Entity<T>): string[] => {
  const outRow: string[] = [];
  getFieldArray(entity)
    .filter(([field, _]) => field.listColumn)
    .forEach(([field, _]) => {
      if (field.type === FieldType.Reference) {
        outRow.push(field.label + ' Id');
        outRow.push(field.label + ' Label');
      } else {
        outRow.push(field.label);
      }
    });
  return outRow;
};

export const getRowValuesForCSV = <T extends Row>(
  state: StateContainingGCache,
  entity: Entity<T>,
  row: T,
): string[] => {
  const outRow: string[] = [];
  const rowValues = row as Record<string, unknown>;
  getFieldArray(entity)
    .filter(([field, _]) => field.listColumn)
    .forEach(([field, fieldId]) => {
      const value = rowValues[fieldId];
      outRow.push(isBlank(value) ? '' : String(value));
      if (field.type === FieldType.Reference) {
        if (isBlank(value)) {
          outRow.push('');
        } else {
          const refEntity = (field as ReferenceField).refEntity;
          const cachedRow = state ? selectRecordFromGCache(state, refEntity.id, String(value))?.data : null;
          const label = getRowLabel(refEntity, 'Static', cachedRow);
          outRow.push(label);
        }
      }
    });
  return outRow;
};

/**
 * Generate CSV string from data rows with automatic reference data prefetching
 *
 * @param store - Redux store with dispatch and getState
 * @param entity - Entity definition
 * @param rows - Data rows to convert
 * @param includeHeader - Whether to include header row
 * @returns Promise that resolves to CSV string
 */
export const generateCSVAsync = async <T extends Row>(
  store: { dispatch: Dispatch; getState: () => { cache: GCacheState } },
  entity: Entity<T>,
  rows: T[],
  includeHeader: boolean,
): Promise<string> => {
  // Pre-fetch all reference data
  await prefetchReferenceData(store, entity, rows, true);

  // Generate CSV with now-populated cache
  return generateCSV(store.getState(), entity, rows, includeHeader);
};

/**
 * Copy a string to the clipboard
 *
 * @param text - Text to copy to clipboard
 */
export const copyStringToClipboard = async (text: string): Promise<void> => {
  if (!text) {
    return;
  }
  debug('Copying to clipboard:', text.substring(0, 100) + (text.length > 100 ? '...' : ''));
  await navigator.clipboard.writeText(text);
  // Small delay to ensure clipboard operation completes
  await new Promise((resolve) => {
    setTimeout(resolve, 250);
  });
};

/**
 * Copy data rows to clipboard as CSV (with reference data prefetching)
 *
 * @param store - Redux store with dispatch and getState
 * @param entity - Entity definition
 * @param rows - Data rows to copy
 * @param includeHeader - Whether to include header row
 */
export const copyDataToClipboard = async <T extends Row>(
  store: { dispatch: Dispatch; getState: () => { cache: GCacheState } },
  entity: Entity<T>,
  rows: T[],
  includeHeader: boolean,
): Promise<void> => {
  const csvString = await generateCSVAsync(store, entity, rows, includeHeader);
  await copyStringToClipboard(csvString);
};
